import { useEffect, useId, useRef, useState } from 'react'
import { Link } from 'react-router'
import { api } from '../api/client'
import type { AuthUser, NotificationPref } from '../api/types'

/** "Bacho Saralidze" is "BS"; a single name or an email address gives its
 *  first letter. */
export function initials(name: string): string {
  const words = name.includes('@')
    ? [name.split('@')[0] ?? name]
    : name.trim().split(/\s+/).filter(Boolean)
  const first = words[0]?.[0] ?? ''
  const last = words.length > 1 ? words[words.length - 1]?.[0] ?? '' : ''
  return (first + last).toUpperCase()
}

interface Props {
  user: AuthUser
  onSignOut: () => void
}

/** The signed-in reader, as initials that open their name, address and
 *  Sign out. The menu hangs from the button inside the header by CSS alone:
 *  it measures nothing on screen, so UI zoom has nothing to correct. */
export function UserMenu({ user, onSignOut }: Props) {
  const name = user.display_name ?? user.email
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const button = useRef<HTMLButtonElement>(null)
  const menuId = useId()

  useEffect(() => {
    if (!open) return
    const onPointer = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      button.current?.focus()
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="user-menu" ref={root}>
      <button ref={button} type="button" className="avatar" aria-label={name}
              aria-expanded={open} aria-controls={menuId}
              onClick={() => setOpen((was) => !was)}>
        {initials(name)}
      </button>
      {open && (
        <div className="user-menu-panel" id={menuId}>
          <p className="user-menu-name">{name}</p>
          {user.display_name && <p className="user-menu-email muted">{user.email}</p>}
          <EmailPref />
          <Link to="/ai-access" onClick={() => setOpen(false)}>AI access</Link>
          <button type="button" className="button" onClick={onSignOut}>Sign out</button>
        </div>
      )}
    </div>
  )
}

/** The reader's own alert email choice (phase 4): on or off, and warnings
 *  and alarms or alarms only. Read when the menu opens, saved on change. */
function EmailPref() {
  const [pref, setPref] = useState<NotificationPref>()
  const [error, setError] = useState<string>()
  useEffect(() => {
    let live = true
    api.alerts.myPref().then((p) => { if (live) setPref(p) })
      .catch((failed: unknown) => { if (live) setError(failed instanceof Error ? failed.message : 'Could not read it.') })
    return () => { live = false }
  }, [])
  const save = async (next: NotificationPref) => {
    const before = pref
    setPref(next)
    try {
      setError(undefined)
      await api.alerts.setMyPref(next)
    } catch (failed) {
      setPref(before)
      setError(failed instanceof Error ? failed.message : 'Not saved.')
    }
  }
  if (!pref) return error ? <p className="error">{error}</p> : null
  return (
    <div className="user-menu-pref">
      <label>
        <input type="checkbox" checked={pref.email}
               onChange={() => void save({ ...pref, email: !pref.email })} />
        Email me about alerts
      </label>
      <select aria-label="Which alerts to email" value={pref.receives} disabled={!pref.email}
              onChange={(e) => void save({ ...pref, receives: e.target.value as NotificationPref['receives'] })}>
        <option value="both">Warnings and alarms</option>
        <option value="alarm">Alarms only</option>
      </select>
      {pref.configured === false && <span className="muted">Email is not set up yet.</span>}
      {error && <span className="error">{error}</span>}
    </div>
  )
}
